import React, { useState, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Download, Loader2, FileSpreadsheet } from "lucide-react";

export default function ImportItemsDialog({ lots, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [lotId, setLotId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const downloadTemplate = () => {
    window.open(`${BACKEND}/api/import/items/template`, "_blank");
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!lotId) { toast.error("Selecione um lote de destino primeiro."); if (inputRef.current) inputRef.current.value = ""; return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(`/import/items?lot_id=${lotId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Importação concluída: ${data.created} criada(s), ${data.skipped} ignorada(s).`);
      onDone?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setResult(null); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="import-items-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-accent" /> Importar mercadorias</DialogTitle>
          <DialogDescription>Envie um arquivo CSV ou XLSX com as colunas do modelo. Linhas com código já existente no lote são ignoradas.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Lote de destino</Label>
            <Select value={lotId} onValueChange={setLotId}>
              <SelectTrigger data-testid="import-lot-select"><SelectValue placeholder="Selecione o lote" /></SelectTrigger>
              <SelectContent>{lots.map((l) => <SelectItem key={l.id} value={l.id}>{l.code}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadTemplate} data-testid="import-template-button"><Download className="h-4 w-4 mr-2" /> Baixar modelo</Button>
            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} data-testid="import-file-input" />
            <Button onClick={() => inputRef.current?.click()} disabled={busy || !lotId} data-testid="import-upload-button">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />} Enviar arquivo
            </Button>
          </div>

          {result && (
            <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm" data-testid="import-result">
              <p><strong>{result.created}</strong> criada(s) · <strong>{result.skipped}</strong> ignorada(s) · <strong>{result.errors?.length || 0}</strong> erro(s) de {result.total} linha(s)</p>
              {result.errors?.length > 0 && (
                <ul className="mt-2 max-h-40 overflow-auto text-xs text-red-600 list-disc pl-5">
                  {result.errors.map((e, i) => <li key={i}>Linha {e.row}: {e.error}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="import-close-button">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
